import { computed } from 'vue'

import { useMediaQuery } from '@vueuse/core'

import {
  ASSISTANT_PANEL_NARROW_WIDTH_PX,
  ASSISTANT_PANEL_WIDTH_PX,
} from '../constants'
import { useShoppingAssistantWeb } from '../stores/useShoppingAssistantWeb'

// Mirrors the `md` Tailwind breakpoint: below it the assistant is a full-screen
// overlay, at/above it a pinned right-edge side panel that squeezes the page.
const DESKTOP_QUERY = '(min-width: 768px)'
// Mirrors the `lg` Tailwind breakpoint: at/above it the panel takes its full
// width; between `md` and `lg` it uses a narrower width so the squeezed page
// keeps enough room.
const WIDE_QUERY = '(min-width: 1024px)'

/**
 * Viewport regime for the web shopping-assistant, shared by the panel host (to
 * size/shape itself) and the page layout (to decide whether and how far to
 * squeeze). Keeping the breakpoints and the panel width in one place stops the
 * host and layout from drifting.
 *
 * - `isDesktop` (≥768): pinned, page-squeezing side panel vs full-screen overlay.
 * - `panelWidthPx`: side-panel width — full at ≥1024, narrower between md and lg.
 * - `isSqueezing`: the page is pushed by `panelWidthPx` whenever the panel is
 *   open on a pinned-panel (≥md) viewport.
 */
export function useShoppingAssistantViewport() {
  const store = useShoppingAssistantWeb()
  const isDesktop = useMediaQuery(DESKTOP_QUERY)
  const isWideDesktop = useMediaQuery(WIDE_QUERY)

  const panelWidthPx = computed(() =>
    isWideDesktop.value
      ? ASSISTANT_PANEL_WIDTH_PX
      : ASSISTANT_PANEL_NARROW_WIDTH_PX,
  )
  const isSqueezing = computed(() => store.isOpen && isDesktop.value)

  return { isDesktop, panelWidthPx, isSqueezing }
}
